import { type FC, useCallback, useEffect, useMemo } from 'react';
import { batch } from 'react-redux';
import { debounce } from 'throttle-debounce';

import { useMapStoreContext } from '@/microfrontends/map/shared/context/mapStoreContext';
import { convertMapBoundsToJsonQueryBbox } from '@/microfrontends/map/shared/utils/map/convertMapBoundsToJsonQueryBbox';

import { boundActionsFactory } from '@/packages/redux-state/store';

import { useGeoFiltersChanged, useMapSizeChanged, useViewModeChangedToList } from './hooks';

const DEBOUNCE_TIMEOUT = 1000;

const useBoundActions = boundActionsFactory(reduxActions => ({
  setMapConfiguration: reduxActions.mapConfiguration.setMapConfiguration,
  updateFiltersBbox: reduxActions.jsonQuery.updateBbox,
}));

/**
 * Переработать этот менеджер, явно можно упростить, пока как будто бы лишняя логика
 */
export const MapBoundsUpdateManager: FC = () => {
  const { map } = useMapStoreContext();
  const actions = useBoundActions();

  const checkMapSizeChanged = useMapSizeChanged();

  const checkGeoFiltersChanged = useGeoFiltersChanged();

  const checkViewModeChangedToList = useViewModeChangedToList();

  const updateMapConfiguration = useCallback(() => {
    batch(() => {
      const bounds = map.getBounds({ useMapMargin: true });

      actions.setMapConfiguration({
        bounds,
        center: map.getCenter(),
        zoom: map.getZoom(),
      });

      actions.updateFiltersBbox(convertMapBoundsToJsonQueryBbox(bounds));
    });
  }, [actions, map]);

  const handleBoundsChange = useCallback(() => {
    const isMapSizeChanged = checkMapSizeChanged();
    const isGeoFiltersChanged = checkGeoFiltersChanged();
    const isListViewMode = checkViewModeChangedToList();

    if (isListViewMode || isGeoFiltersChanged || isMapSizeChanged) {
      return;
    }

    updateMapConfiguration();
  }, [checkGeoFiltersChanged, checkMapSizeChanged, checkViewModeChangedToList, updateMapConfiguration]);

  const handleBoundsChangeDebounced = useMemo(
    () => debounce(DEBOUNCE_TIMEOUT, handleBoundsChange),
    [handleBoundsChange],
  );

  useEffect(() => {
    map.events.add('boundschange', handleBoundsChangeDebounced);

    return (): void => {
      map.events.remove('boundschange', handleBoundsChangeDebounced);
    };
  }, [map.events, handleBoundsChangeDebounced]);

  return null;
};
